import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const navLinks = [
  { to: "/", label: "Главная" },
  { to: "/catalog", label: "Каталог" },
  { to: "/podbor", label: "Подбор" },
  { to: "/services", label: "Услуги" },
  { to: "/reviews", label: "Отзывы" },
  { to: "/blog", label: "Блог" },
  { to: "/about", label: "О нас" },
  { to: "/contacts", label: "Контакты" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const settings = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  if (location.pathname.startsWith("/admin") || location.pathname === "/login") return null;

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 w-full z-50 transition-all duration-500 border-b border-white/5"
      style={{
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        background: scrolled ? "rgba(5,5,5,0.85)" : "rgba(5,5,5,0.6)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/favicon.png" alt={settings?.logo_text || "AutoPride"} className="w-12 h-12 drop-shadow-lg" />
          <span data-testid="nav-logo-text" className="font-semibold text-xl tracking-wide text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {settings?.logo_text || "AUTOPRIDE"}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.to.replace("/", "") || "home"}`}
              className={`text-sm font-semibold tracking-wide uppercase transition-colors duration-300 drop-shadow-sm ${
                location.pathname === link.to
                  ? "text-[#D4AF37]"
                  : "text-white hover:text-[#D4AF37]"
              }`}
              style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}
            >
              {link.label}
            </Link>
          ))}
          {isAuthenticated && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="text-sm font-medium tracking-wide uppercase text-[#D4AF37] hover:text-[#F4D03F] transition-colors"
            >
              Админ
            </Link>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Закрыть меню" : "Открыть меню"}
          aria-expanded={open}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <nav className="md:hidden glass border-t border-white/10 px-6 py-6 space-y-4 relative z-50" aria-label="Мобильное меню">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`block text-sm font-medium tracking-wide uppercase ${
                  location.pathname === link.to ? "text-[#D4AF37]" : "text-[#A3A3A3]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAuthenticated && (
              <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm font-medium tracking-wide uppercase text-[#D4AF37]">
                Админ
              </Link>
            )}
          </nav>
        </>
      )}
    </nav>
  );
}
